/**
 * Composes form and reconciliation state for the invoice detail page.
 * Pass getReconciliation so form's mutation onSuccess can update reconciliation state.
 */
import { useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RecentView } from '../../utils/recentActivity';
import type { InvoiceDetailResponse, ReconciliationMatchNote } from '../../types/invoice';
import type { LocationDefaultTaxRate } from '../useGetSettings';
import type { NexReconciliationIdentifierMode } from '../../utils/invoiceNexIdentifierUi';
import type { ReconciliationStateForForm } from './useInvoiceDetailFormState';
import { useInvoiceDetailFormState } from './useInvoiceDetailFormState';
import { useInvoiceDetailReconciliationState } from './useInvoiceDetailReconciliationState';
import type { UpdateInvoiceInput } from './useUpdateInvoice';
import { manualMatchesFromOverrides } from '../../utils/nexLineDisplay';
import { computeReturnSlipSubmitPreview, type ReturnSlipSubmitPreviewResult } from '../../utils/returnSlipSubmitPreview';
import type { UnmatchedLineItemsNexSubmitMode } from '../../utils/unmatchedLineItemsNexSubmitMode';
import type { InvoiceMismatchFlagFamilyPolicy } from '../../utils/invoiceFlagFamilyPolicy';

export interface UseInvoiceDetailStateOptions {
    queryClient: ReturnType<typeof useQueryClient>;
    trackView: (view: Omit<RecentView, 'ts'>) => void;
    locationDefaultTaxRates?: LocationDefaultTaxRate[];
    shopLocations?: Array<{ _id: string; state: string }>;
    onAfterVendorMismatchResolved?: (ctx: {
        nexVendorName: string;
        invoiceVendorSnapshot: string;
        internalSupplierId?: string | null;
        nexVendorId?: number;
    }) => void;
    unmatchedLineItemsNexSubmitMode?: UnmatchedLineItemsNexSubmitMode;
    nexReconciliationIdentifierMode?: NexReconciliationIdentifierMode;
    mismatchFlagFamilyPolicy?: InvoiceMismatchFlagFamilyPolicy;
    /** Org setting: US sales tax as AP dist line. Default false. */
    salesTaxAsLineItem?: boolean;
    onTeachableAmountCommitted?: (field: 'taxesTotal' | 'orderTotal', value: number) => void;
}

export function useInvoiceDetailState(
    id: string | undefined,
    invoice: InvoiceDetailResponse | undefined,
    options: UseInvoiceDetailStateOptions
) {
    const {
        queryClient,
        trackView,
        locationDefaultTaxRates,
        shopLocations,
        onAfterVendorMismatchResolved,
        unmatchedLineItemsNexSubmitMode,
        nexReconciliationIdentifierMode = 'ro',
        mismatchFlagFamilyPolicy,
        salesTaxAsLineItem = false,
        onTeachableAmountCommitted,
    } = options;
    const reconRef = useRef<ReconciliationStateForForm | null>(null);

    const form = useInvoiceDetailFormState(id, invoice, {
        id,
        getReconciliation: () => reconRef.current ?? undefined,
        queryClient,
        trackView,
        locationDefaultTaxRates,
        shopLocations,
        unmatchedLineItemsNexSubmitMode,
        nexReconciliationIdentifierMode,
        salesTaxAsLineItem,
        onTeachableAmountCommitted,
    });

    const recon = useInvoiceDetailReconciliationState(id, invoice, {
        editedFields: form.editedFields,
        updateLineItem: form.updateLineItem,
        updateField: form.updateField,
        getLineItemStringValue: form.getLineItemStringValue,
        getIgnoredPartStringValue: form.getIgnoredPartStringValue,
        lineItemBackendIndexByCurrentIndex: form.lineItemBackendIndexByCurrentIndex,
        lineItemsUseDirectIndex: form.lineItemsUseDirectIndex,
        savedFieldsRef: form.savedFieldsRef,
        isMutatingRef: form.isMutatingRef,
        updateInvoiceMutation: form.updateInvoiceMutation,
        suggestionModal: form.suggestionModal,
        setSuggestionModal: form.setSuggestionModal,
        vendorSuggestionModal: form.vendorSuggestionModal,
        setVendorSuggestionModal: form.setVendorSuggestionModal,
        localLineArithmeticIssues: form.localLineArithmeticIssues,
        onAfterVendorMismatchResolved,
        mismatchFlagFamilyPolicy,
    });

    reconRef.current = {
        unmatchedPartIndices: recon.unmatchedPartIndices,
        localMatchedNexOverrides: recon.localMatchedNexOverrides,
        localReconciliationMatchNotes: recon.localReconciliationMatchNotes,
        setUnmatchedPartIndices: recon.setUnmatchedPartIndices,
        setLocalMatchedNexOverrides: recon.setLocalMatchedNexOverrides,
        setPreviouslyMatchedNexLines: recon.setPreviouslyMatchedNexLines,
        setExpandedMatchedNexRows: recon.setExpandedMatchedNexRows,
        setExpandedMatchedNexIgnoredRows: recon.setExpandedMatchedNexIgnoredRows,
    };

    const effectiveReconciliationMatchNotes = useMemo((): ReconciliationMatchNote[] => {
        const base = (form.effectiveReconciliationMatchNotes ?? []) as ReconciliationMatchNote[];
        const localNotes = Object.values(recon.localReconciliationMatchNotes ?? {});
        if (localNotes.length === 0) return base;
        const byPartIndex = new Map(base.map((n) => [n.partIndex, n] as const));
        for (const note of localNotes) {
            const prev = byPartIndex.get(note.partIndex);
            if (!prev) {
                byPartIndex.set(note.partIndex, note);
                continue;
            }
            const reasonParts = [prev.reason, note.reason].filter(
                (s) => typeof s === 'string' && s.trim() !== '',
            );
            byPartIndex.set(note.partIndex, {
                ...prev,
                ...note,
                reason: reasonParts.join('\n\n'),
                extractedFromPage: note.extractedFromPage ?? prev.extractedFromPage,
            });
        }
        return Array.from(byPartIndex.values());
    }, [form.effectiveReconciliationMatchNotes, recon.localReconciliationMatchNotes]);

    /** Computed here (not in form state) so we read current `recon` — form runs before reconciliation and only sees one-frame-stale `reconRef`. */
    const returnSlipSubmitPreview = useMemo<ReturnSlipSubmitPreviewResult | null>(() => {
        if (!invoice || (form.editedFields.invoiceType ?? invoice.invoiceType) !== 'return_slip') return null;
        return computeReturnSlipSubmitPreview({
            invoice,
            editedFields: form.editedFields,
            unmatchedPartIndices: recon.unmatchedPartIndices,
            localMatchedNexOverrides: recon.localMatchedNexOverrides,
        });
    }, [invoice, form.editedFields, recon.unmatchedPartIndices, recon.localMatchedNexOverrides]);

    const returnSlipSubmitReady = useMemo(() => {
        if (!invoice || (form.editedFields.invoiceType ?? invoice.invoiceType) !== 'return_slip') return true;
        const ro = String((form.editedFields.roNumber ?? invoice.roNumber ?? '')).trim();
        if (!ro || Number.isNaN(parseInt(ro, 10))) return false;
        return returnSlipSubmitPreview?.ok === true;
    }, [invoice, form.editedFields, returnSlipSubmitPreview]);

    const handleRefreshNexsyisData = (strategy?: 'po_first' | 'ro_only') => {
        if (!id || !invoice) return;
        const ro = String((form.editedFields.roNumber ?? invoice.roNumber ?? '')).trim();
        const po = String((form.editedFields.purchaseOrderNumber ?? invoice.purchaseOrderNumber ?? '')).trim();
        const shopRo = String((form.editedFields.shopRoNumber ?? invoice.shopRoNumber ?? '')).trim();
        if (!ro && !po && !shopRo) return;
        form.isMutatingRef.current = true;
        form.isAutoSaveRef.current = false;
        const invoiceType = form.editedFields.invoiceType ?? invoice?.invoiceType;
        const manualMatchesArray = manualMatchesFromOverrides(recon.localMatchedNexOverrides, invoiceType);
        const fieldsToSave: Partial<InvoiceDetailResponse> = {
            ...form.editedFields,
            unmatchedPartIndices: Array.from(recon.unmatchedPartIndices),
            manualMatches: manualMatchesArray,
        };
        form.savedFieldsRef.current = { ...fieldsToSave };
        form.updateInvoiceMutation.mutate(
            {
                invoiceId: id,
                updatedFields: fieldsToSave,
                refreshNexsyisData: true,
                ...(strategy ? { refreshNexsyisStrategy: strategy } : {}),
            } as UpdateInvoiceInput,
            {
                onSuccess: () => {
                    recon.setSelectedNexOptionKey(null);
                    recon.setSelectedNexOptionAccordionKey(null);
                },
            }
        );
    };

    const submitPayloadPreview = form.submitPayloadPreview;

    const handleSubmitConfirm = () => {
        if (!id) return;
        form.setSubmitConfirmOpen(false);

        const invoiceType = form.editedFields.invoiceType ?? invoice?.invoiceType;
        const manualMatchesArray = manualMatchesFromOverrides(recon.localMatchedNexOverrides, invoiceType);

        const remappedReconciliationIssues = form.buildCurrentReconciliationIssuesForSave();
        const fieldsToSubmit: Partial<InvoiceDetailResponse> = {
            ...form.editedFields,
            unmatchedPartIndices: Array.from(recon.unmatchedPartIndices),
            /** Always replace server `manualMatches` so stale indices are not merged on submit (see updateInvoice). */
            manualMatches: manualMatchesArray,
            ...(remappedReconciliationIssues ? { reconciliationIssues: remappedReconciliationIssues } : {}),
        };
        const manualPoHeaderOverrides: Record<string, number> = {};
        for (const [k, v] of Object.entries(form.manualPoHeaderSelections ?? {})) {
            if (typeof v === 'number' && Number.isFinite(v)) {
                manualPoHeaderOverrides[String(k)] = v;
            }
        }
        form.savedFieldsRef.current = { ...fieldsToSubmit };
        form.updateInvoiceMutation.mutate({
            invoiceId: id,
            markAsManuallySubmitted: true,
            updatedFields: fieldsToSubmit,
            manualPoHeaderOverrides,
        });
    };

    return {
        ...form,
        ...recon,
        effectiveReconciliationMatchNotes,
        submitPayloadPreview,
        handleSubmitConfirm,
        handleRefreshNexsyisData,
        returnSlipSubmitPreview,
        returnSlipSubmitReady,
    };
}
